<template>
  <StartWorkCheckHazardList
    v-if="currentStep === 0"
    v-model="startWorkCheckStore.checkTasks.at(1).hazards"
    :title="t(useTranslationPath(callerPath, 'hazards'))"
    :list
    :action-label="t(useTranslationPath(callerPath, 'next'))"
    @action-click="goNext"
  />
  <template v-else-if="currentStep <= totalSteps">
    <AppContainer>
      <div>
        <EPageTitleMobile
          :title="currentHazardTitle"
          :description="
            currentHazardQuestions.length === 0
              ? t(useTranslationPath(callerPath, 'noTasksForHazard'))
              : ''
          "
        />

        <div class="space-y-4">
          <AttachmentsList v-if="currentAttachments.length > 0" :attachments="currentAttachments" />

          <EForm v-if="currentHazardQuestions.length">
            <template
              v-for="({ index, hazard }, offset) in currentHazardQuestions"
              :key="`hazard${index}`"
            >
              <EFormField
                :id="`radio${offset}`"
                :name="`radio${offset}`"
                :label="hazard.task"
                fieldset
              >
                <ERadioGroup
                  v-model="startWorkCheckStore.checkTasks.at(1).tasks[index].value"
                  inline
                >
                  <ERadio
                    :id="`radio${offset}Yes`"
                    :name="`radio${offset}Yes`"
                    :label="t(useTranslationPath(callerPath, 'yes'))"
                    :value="true"
                  />
                  <ERadio
                    :id="`radio${offset}No`"
                    :name="`radio${offset}No`"
                    :label="t(useTranslationPath(callerPath, 'no'))"
                    :value="false"
                  />
                </ERadioGroup>
              </EFormField>
            </template>
          </EForm>
        </div>
      </div>

      <template #bottom>
        <EButton
          :label="t(useTranslationPath(callerPath, 'next'))"
          block
          :disabled="isActionDisabled"
          @click="goNext"
        />
      </template>
    </AppContainer>
  </template>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import AttachmentsList from '@/components/AttachmentsList.vue'
import StartWorkCheckHazardList from '@/components/WorkOrder/StartWorkCheck/StartWorkCheckHazardList.vue'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useStartWorkCheckStore } from '@/stores/authenticated/StartWorkCheckStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore'
import { EButton, EForm, EFormField, EPageTitleMobile, ERadio, ERadioGroup } from '@expansivefm/eds'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const { setCustomGoBack } = useGoBack()

const router = useRouter()
const appStore = useAppStore()
const workOrderStore = useWorkOrderStore()
const startWorkCheckStore = useStartWorkCheckStore()

const callerPath = 'views.authenticated.workOrders.startWorkChecks.hazards'

const currentStep = ref(0)

const list = computed(() => startWorkCheckStore.checkTasks.at(1).hazards)

const totalSteps = computed(() => list.value.filter((hazard) => hazard.value === true).length)

const currentHazardTitle = computed(() => {
  return list.value.filter((hazard) => hazard.value === true).at(currentStep.value - 1)?.name
})

const currentHazardQuestions = computed(() => {
  return startWorkCheckStore.checkTasks
    .at(1)
    .tasks.map((hazard, index) => ({ index, hazard }))
    .filter(({ hazard }) => {
      return (
        list.value.filter((hazard) => hazard.value === true).at(currentStep.value - 1).id ===
        hazard.hazard_id
      )
    })
})

const currentAttachments = computed(() => {
  return (
    startWorkCheckStore.checkTasks
      .at(1)
      .hazards.filter((hazardItem) => {
        return (
          list.value.filter((hazard) => hazard.value === true).at(currentStep.value - 1).id ===
          hazardItem.id
        )
      })
      .at(0)?.attachments || []
  )
})

const isActionDisabled = computed(() => {
  if (currentStep.value > 0 && currentHazardQuestions.value.length > 0) {
    const checks = startWorkCheckStore.checkTasks
      .at(1)
      .tasks.filter(
        (task) =>
          list.value.filter((hazard) => hazard.value === true).at(currentStep.value - 1).id ===
          task.hazard_id,
      )
      .every((task) => task.value !== null)

    return !checks
  }

  return false
})

onMounted(async () => {
  await setupHazards()

  setCustomGoBack(() => {
    router.push({
      name: 'work-order.start-work-checks.index',
      params: {
        workOrderId: workOrderStore.workOrder.id,
      },
    })
  })

  appStore.$patch({ showBackButton: true })
})

watch(
  () => router.currentRoute.value,
  (newQuery) => {
    if ('step' in newQuery.query && parseInt(newQuery.query?.step) !== currentStep.value) {
      const queryStep = parseInt(newQuery.query.step)

      if (queryStep < 0 || queryStep > totalSteps.value) {
        currentStep.value = 0
        setCustomGoBack(() => {
          router.push({
            name: 'work-order.start-work-checks.index',
            params: {
              workOrderId: workOrderStore.workOrder.id,
            },
          })
        })
      } else {
        currentStep.value = queryStep
        setCustomGoBack(goBack)
      }
    }
  },
  { immediate: true, deep: true },
)

watch(
  () => startWorkCheckStore.checkTasks.at(1).hazards,
  (newList) => {
    newList.forEach((hazard) => {
      if (
        hazard.value === true &&
        startWorkCheckStore.checkTasks
          .at(1)
          .tasks.find((section) => section.hazard_id === hazard.id) === undefined
      ) {
        startWorkCheckStore.checkHazards
          ?.filter((hazardQuestions) => hazardQuestions.hazard_id === hazard.id)
          ?.forEach((hazardQuestions) => {
            startWorkCheckStore.checkTasks.at(1).tasks.push({
              ...hazardQuestions,
              value: null,
              disabled: false,
            })
          })
      } else if (
        (hazard.value === false || hazard.value === null) &&
        startWorkCheckStore.checkTasks
          .at(1)
          .tasks.find((section) => section.hazard_id === hazard.id) !== undefined
      ) {
        startWorkCheckStore.checkTasks.at(1).tasks = startWorkCheckStore.checkTasks
          .at(1)
          .tasks.filter((section) => section.hazard_id !== hazard.id)
      }
    })
  },
  { deep: true },
)

const setupHazards = async () => {
  await workOrderStore
    .fetchWorkOrder(workOrderStore.workOrder.id, [
      'workOrderHazards.hazard',
      'workOrderHazards.media',
    ])
    .then(({ data }) => {
      if (startWorkCheckStore.checkTasks.at(1).hazards.length === 0) {
        startWorkCheckStore.checkTasks.at(1).hazards = startWorkCheckStore.hazards.map((hazard) => {
          const hasQuestions =
            data.workOrderHazards?.find((checkHazard) => checkHazard.hazard.id === hazard.id) !==
            undefined

          const attachments =
            data.workOrderHazards?.find((checkHazard) => checkHazard.hazard.id === hazard.id)
              ?.media || []

          if (hasQuestions) {
            const questions = startWorkCheckStore.checkHazards
              .filter((hazardQuestions) => hazardQuestions.hazard_id === hazard.id)
              .map((hazardQuestions) => ({
                ...hazardQuestions,
                value: null,
                disabled: false,
              }))

            startWorkCheckStore.checkTasks.at(1).tasks = [
              ...startWorkCheckStore.checkTasks.at(1).tasks,
              ...questions,
            ]
          }

          return {
            ...hazard,
            value: hasQuestions,
            disabled: hasQuestions,
            attachments,
          }
        })
      }
    })
}

const goBack = () => {
  currentStep.value -= 1

  if (currentStep.value < 0) {
    router.push({
      name: `work-order.start-work-checks.hazards`,
      params: {
        workOrderId: workOrderStore.workOrder.id,
      },
    })
  } else {
    router.push({
      name: `work-order.start-work-checks.hazards`,
      params: {
        workOrderId: workOrderStore.workOrder.id,
      },
      query: {
        step: currentStep.value,
      },
    })
  }
}

const goNext = () => {
  currentStep.value += 1

  if (currentStep.value > totalSteps.value) {
    router.push({
      name: `work-order.start-work-checks.index`,
      params: {
        workOrderId: workOrderStore.workOrder.id,
      },
    })
  } else {
    router.push({
      name: `work-order.start-work-checks.hazards`,
      params: {
        workOrderId: workOrderStore.workOrder.id,
      },
      query: {
        step: currentStep.value,
      },
    })
  }
}
</script>
